import type { WorktreeLayoutFile } from '../worktree-layout-file.js';
import { discoverSessionBindingAsync } from './discover-session-binding.js';
import {
  ensureRepoCheckoutPathsForSession,
  ensureRepoCheckoutPathsForSessionAsync,
  resolveIsolatedSessionParentPath,
} from './resolve-isolated-session-parent-path.js';
import { resolveSessionParentPathForPrompt as resolveSessionParentPathForPromptImpl } from './resolve-session-parent-path-for-prompt.js';
import type { SessionWorktreeCache } from './session-worktree-cache.js';

export type SessionWorktreePathsContext = {
  worktreesRootPath: string;
  log: (msg: string) => void;
  cache: SessionWorktreeCache;
  layout: WorktreeLayoutFile;
};

export function createSessionWorktreePathsApi(ctx: SessionWorktreePathsContext) {
  const discoverAsync = (sessionId: string) =>
    discoverSessionBindingAsync({
      sessionId,
      worktreesRootPath: ctx.worktreesRootPath,
      layout: ctx.layout,
    });

  return {
    discoverAsync,
    getIsolatedSessionParentPathForSession(sessionId: string | undefined): string | null {
      return resolveIsolatedSessionParentPath(sessionId, ctx.cache, (sid) =>
        ensureRepoCheckoutPathsForSession(sid, ctx.cache),
      );
    },
    resolveSessionParentPathForPrompt(
      sessionId: string | undefined,
      opts: Parameters<typeof resolveSessionParentPathForPromptImpl>[0]['opts'],
    ): Promise<string | undefined> {
      return resolveSessionParentPathForPromptImpl({
        sessionId,
        cache: ctx.cache,
        worktreesRootPath: ctx.worktreesRootPath,
        layout: ctx.layout,
        log: ctx.log,
        discover: (sid) => discoverAsync(sid),
        opts,
      });
    },
    usesWorktreeSession(sessionId: string | undefined): boolean {
      if (!sessionId) return false;
      return ctx.cache.hasSession(sessionId);
    },
    getRepoCheckoutPathsForSession(sessionId: string | undefined): string[] | undefined {
      if (!sessionId) return undefined;
      return ctx.cache.getRepoCheckoutPaths(sessionId);
    },
    ensureRepoCheckoutPathsForSession(sessionId: string | undefined): string[] | undefined {
      return ensureRepoCheckoutPathsForSession(sessionId, ctx.cache);
    },
    async ensureRepoCheckoutPathsForSessionAsync(sessionId: string | undefined): Promise<string[] | undefined> {
      return ensureRepoCheckoutPathsForSessionAsync(sessionId, ctx.cache, (sid) => discoverAsync(sid));
    },
    getSessionWorktreeRootForSession(sessionId: string | undefined): string | null {
      return resolveIsolatedSessionParentPath(sessionId, ctx.cache, (sid) =>
        ensureRepoCheckoutPathsForSession(sid, ctx.cache),
      );
    },
  };
}
